/**
 * Вкладка «Цена» карточки сделки: каждая правка цены товара договора — кто,
 * когда и в какой период (оформление договора, повторная примерка, накануне,
 * день выдачи, после выдачи) на фоне опорных событий. Спека
 * `docs/specs/2026-09-25-deal-price-design.md`; те же данные —
 * `prokat-runner deal-price ID`.
 */
import { useQuery } from "@tanstack/react-query";
import { TriangleAlert } from "lucide-react";
import type { ReactNode } from "react";

import { type DealPrice, type PriceAnchor, type PriceChange, type PricePeriod, type PriceProduct, loadDealPrice } from "@/lib/dealPrice";

const PERIOD: Record<PricePeriod, string> = {
  after: "после выдачи",
  issue: "в день выдачи",
  eve: "накануне выдачи",
  repeat: "в день повторной примерки",
  contract: "в день оформления договора",
  between: "промежуточные дни",
};
const PERIOD_TONE: Record<PricePeriod, string> = {
  after: "border-err text-err",
  issue: "border-err text-err",
  eve: "border-warn text-warn",
  repeat: "border-border text-fg-muted",
  contract: "border-border text-fg-muted",
  between: "border-border text-fg-muted",
};
const BASIS: Record<string, string> = { plan: "по договору", fact: "по факту выдачи", both: "по договору и по факту" };

const rub = (n: number) => `${n.toLocaleString("ru-RU")} ₽`;
const signedRub = (n: number) => (n > 0 ? `+${rub(n)}` : rub(n));
const dt = (iso: string | null) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)} ${iso.slice(11, 16)}` : "—");
const day = (iso: string | null) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}` : "—");

function Stat({ label, value, hint, tone = "" }: { label: string; value: ReactNode; hint?: string; tone?: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-1 rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3" data-annot={`цена «${label}»`}>
      <div className="text-[12px] text-fg-muted">{label}</div>
      <div className={`text-[20px] font-semibold leading-none tabular-nums ${tone}`}>{value}</div>
      {hint && <div className="text-[11.5px] text-fg-faint">{hint}</div>}
    </div>
  );
}

/** Уточнения к правке человеческими словами. */
function notes(c: PriceChange): string[] {
  const out: string[] = [];
  for (const n of c.notes) {
    if (n.startsWith("issue_basis:")) out.push(BASIS[n.slice(12)] ?? n);
    else if (n === "mid_issue") out.push("после первой выдачи в тот же день");
    else if (n === "on_add") out.push("сразу при добавлении товара");
    else if (n === "restored") out.push("цену вернули в течение 10 мин");
    else if (n === "robot") out.push("пересчёт роботом CRM");
    else if (n.startsWith("below_min:")) out.push(`ниже минимальной (${n.slice(10)})`);
  }
  if (c.period === "between" && c.days_before) out.push(`за ${c.days_before} дн. до старта`);
  return out;
}

function productLabel(p: PriceProduct) {
  return (
    <>
      <span className="font-mono">{p.serial ?? "—"}</span>
      {p.name && <span> {p.name}</span>}
    </>
  );
}

type Row = { at: string; anchor?: PriceAnchor; change?: PriceChange; product?: PriceProduct };

function AnchorRow({ a }: { a: PriceAnchor }) {
  const strong = a.kind === "issue" || a.kind === "rent_start";
  return (
    <li className="grid grid-cols-[5.5rem_1fr] gap-x-2 border-t border-border bg-bg-subtle px-3 py-1.5 first:border-t-0" data-annot={`опорное событие ${a.kind}`}>
      <span className="whitespace-nowrap text-[12px] tabular-nums text-fg-faint">{a.kind.startsWith("rent_") ? day(a.at) : dt(a.at)}</span>
      <div className="min-w-0 text-[12.5px]">
        <span className={strong ? "font-medium" : "text-fg-muted"}>{a.title}</span>
        {a.text && <span className="text-fg-muted"> · {a.text}</span>}
        {a.who && <span className="text-fg-faint"> · {a.who}</span>}
      </div>
    </li>
  );
}

function ChangeRow({ c, p }: { c: PriceChange; p: PriceProduct }) {
  const bg = c.alert === "high" ? "bg-err-bg" : "";
  const n = notes(c);
  return (
    <li className={`grid grid-cols-[5.5rem_1fr] gap-x-2 border-t border-border px-3 py-2 first:border-t-0 ${bg}`} data-annot={`правка цены ${p.serial} ${dt(c.at)}`}>
      <span className="whitespace-nowrap pt-px text-[12px] tabular-nums text-fg-faint">{dt(c.at)}</span>
      <div className="min-w-0 text-[12.5px]">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className={`whitespace-nowrap rounded-[var(--radius-ui)] border px-1.5 text-[11px] ${PERIOD_TONE[c.period]}`}>{PERIOD[c.period]}</span>
          <span>{productLabel(p)}:</span>
          <span className="tabular-nums">
            {rub(c.old)} → <span className="font-medium">{rub(c.new)}</span>{" "}
            <span className={c.delta < 0 ? "text-err" : "text-ok"}>({signedRub(c.delta)})</span>
          </span>
          {c.who && <span className="text-fg-muted">{c.who}</span>}
        </div>
        {c.alert && (
          <div className={`mt-0.5 flex items-center gap-1 text-[12px] font-medium ${c.alert === "high" ? "text-err" : "text-warn"}`}>
            <TriangleAlert size={12} aria-hidden />
            {c.alert === "high" ? "снижена цена, которая уже стояла" : "снижена при оформлении"}
          </div>
        )}
        {n.length > 0 && <div className="text-[12px] text-fg-muted">{n.join(" · ")}</div>}
      </div>
    </li>
  );
}

function Products({ items }: { items: PriceProduct[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full rounded-[var(--radius-card)] border border-border text-[12.5px]" data-annot="цены по товарам">
        <thead className="text-left text-[12px] text-fg-muted">
          <tr>
            <th className="px-3 py-1 font-normal">Товар</th>
            <th className="px-3 py-1 font-normal">Добавлен</th>
            <th className="px-3 py-1 text-right font-normal">Первая цена</th>
            <th className="px-3 py-1 text-right font-normal">Сейчас</th>
            <th className="px-3 py-1 text-right font-normal">Правок</th>
          </tr>
        </thead>
        <tbody>
          {items.map((p) => {
            const d = p.current - p.first;
            return (
              <tr key={p.id} className={`border-t border-border align-top ${p.deleted ? "text-fg-faint line-through" : ""}`}>
                <td className="px-3 py-1.5">
                  <div className="flex items-center gap-2">
                    {p.image ? (
                      <img src={p.image} alt={`серия ${p.serial}`} loading="lazy" className="h-10 w-8 shrink-0 rounded-[var(--radius-ui)] border border-border object-cover" />
                    ) : (
                      <div className="h-10 w-8 shrink-0 rounded-[var(--radius-ui)] border border-dashed border-border" aria-hidden />
                    )}
                    <div>
                      {productLabel(p)}
                      {p.is_sale && <span className="ml-2 text-[11.5px] text-accent">продажа</span>}
                      {p.service && <span className="ml-2 text-[11.5px] text-fg-faint">услуга</span>}
                    </div>
                  </div>
                </td>
                <td className="whitespace-nowrap px-3 py-1.5 text-fg-muted">
                  {dt(p.added.at)} · {p.added.who ?? "—"}
                  {p.added.type && <div className="text-[11.5px] text-fg-faint">{p.added.type}</div>}
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums">{rub(p.first)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">
                  {rub(p.current)}
                  {d !== 0 && <div className={`text-[11.5px] ${d < 0 ? "text-err" : "text-ok"}`}>{signedRub(d)}</div>}
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums text-fg-muted">{p.changes.length}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Body({ r }: { r: DealPrice }) {
  const k = r.contract;
  if (!k) return <div className="text-[12.5px] text-fg-muted">Договора по сделке нет — цен нет.</div>;
  const t = r.total;
  const periods = (Object.keys(PERIOD) as PricePeriod[]).filter((p) => t.down_by_period[p]);
  const rows: Row[] = [
    ...r.anchors.map((a) => ({ at: a.at, anchor: a })),
    ...r.products.flatMap((p) => p.changes.map((c) => ({ at: c.at, change: c, product: p }))),
  ].sort((a, b) => a.at.localeCompare(b.at));
  return (
    <>
      <div className="text-[12.5px] text-fg-muted" data-annot="договор цены">
        Договор {k.id} · {k.location ?? "—"} · прокат по договору {day(k.rent_start)} – {day(k.rent_end)} · по факту{" "}
        {k.fact_start ? `${dt(k.fact_start)} – ${k.fact_end ? dt(k.fact_end) : "на руках"}` : "не выдан"}
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat label="Цена договора" value={rub(t.current)} hint={`была ${rub(t.first)} · без удалённых товаров`} />
        <Stat
          label="Снижено"
          value={rub(t.down)}
          tone={t.down ? "text-err" : ""}
          hint={periods.map((p) => `${PERIOD[p]} ${rub(t.down_by_period[p]!)}`).join(" · ") || "снижений не было"}
        />
        <Stat label="Повышено" value={rub(t.up)} tone={t.up ? "text-ok" : ""} />
        <Stat
          label="Тревог"
          value={t.alerts.high}
          tone={t.alerts.high ? "text-err" : ""}
          hint={`снижение накануне, в день выдачи и после · ещё ${t.alerts.low} при оформлении`}
        />
      </div>

      <section className="flex flex-col gap-1.5" data-annot="хронология цены">
        <h2 className="text-[13px] font-medium">Правки цены на фоне событий сделки</h2>
        {rows.some((x) => x.change) ? null : <div className="text-[12.5px] text-fg-muted">Цену после добавления товаров не меняли.</div>}
        <ol className="rounded-[var(--radius-card)] border border-border">
          {rows.map((x, i) => (x.change ? <ChangeRow key={i} c={x.change} p={x.product!} /> : <AnchorRow key={i} a={x.anchor!} />))}
        </ol>
      </section>

      <section className="flex flex-col gap-1.5">
        <h2 className="text-[13px] font-medium">По товарам</h2>
        <Products items={r.products} />
      </section>
    </>
  );
}

export function DealPriceTab({ dealId }: { dealId: number }) {
  const q = useQuery({ queryKey: ["deal-price", dealId], queryFn: () => loadDealPrice(dealId) });
  if (q.isPending) return <div className="text-[12.5px] text-fg-muted">Собираю цены из CRM…</div>;
  if (q.isError) return <div className="text-[12.5px] text-err">Не удалось получить цены: {String(q.error.message)}</div>;
  return <Body r={q.data} />;
}
